import { useEffect, useRef, useState } from "react";

/**
 * Keeps a boolean flag visible for at least `minVisibleMs` after it flips to
 * true. Useful for loading / refetch indicators on fast networks where the
 * real spinner would flash for only a few milliseconds.
 */
export function useDelayedFlag(active: boolean, minVisibleMs = 400): boolean {
  const [visible, setVisible] = useState(active);
  const startedAtRef = useRef<number | null>(active ? Date.now() : null);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (active) {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (startedAtRef.current === null) startedAtRef.current = Date.now();
      setVisible(true);
      return;
    }
    if (startedAtRef.current === null) {
      setVisible(false);
      return;
    }
    const elapsed = Date.now() - startedAtRef.current;
    const remaining = Math.max(0, minVisibleMs - elapsed);
    if (remaining === 0) {
      startedAtRef.current = null;
      setVisible(false);
      return;
    }
    timeoutRef.current = window.setTimeout(() => {
      timeoutRef.current = null;
      startedAtRef.current = null;
      setVisible(false);
    }, remaining);
    return () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [active, minVisibleMs]);

  return visible;
}

/**
 * Holds a flag true for `durationMs` every time `trigger` changes (shallow
 * equality). Useful for showing a refetch indicator when a list query re-runs,
 * even if the network response is so fast that `isFetching` flips back before
 * the UI can paint.
 */
export function usePulseOnChange(trigger: string, durationMs = 500): boolean {
  const [pulsing, setPulsing] = useState(false);
  const firstRef = useRef(true);

  useEffect(() => {
    if (firstRef.current) {
      firstRef.current = false;
      return;
    }
    setPulsing(true);
    const handle = window.setTimeout(() => setPulsing(false), durationMs);
    return () => window.clearTimeout(handle);
  }, [trigger, durationMs]);

  return pulsing;
}
